// Sync status controller.
//
// Aggregates `navigator.onLine`, the queue's size, and whether a drain is
// currently in flight into a single 3-state value that the UI dot consumes.

import type { SyncQueue } from "./sync-queue";

export type SyncStatus = "synced" | "syncing" | "offline";

export interface SyncStatusController {
  get(): SyncStatus;
  on(event: "change", fn: (s: SyncStatus) => void): () => void;
  /**
   * Imperative override used by the repo while a drain is running so the
   * controller can flip to `syncing` without polling the queue.
   */
  setDraining(draining: boolean): void;
  /** Release window listeners. Pages should call this from their cleanup. */
  destroy(): void;
}

export function createSyncStatus(queue: SyncQueue): SyncStatusController {
  const listeners = new Set<(s: SyncStatus) => void>();
  let last: SyncStatus = compute(false, 0);
  let draining = false;
  // We cache queue size from the latest change event rather than reading the
  // IDB store on every status read.
  let queueSize = 0;

  function compute(isDraining: boolean, qSize: number): SyncStatus {
    if (!navigator.onLine) return "offline";
    if (isDraining) return "syncing";
    if (qSize > 0) return "syncing";
    return "synced";
  }

  function recompute(): void {
    const next = compute(draining, queueSize);
    if (next === last) return;
    last = next;
    for (const fn of listeners) {
      try {
        fn(next);
      } catch (err) {
        console.error("[sync-status] listener threw", err);
      }
    }
  }

  async function refreshQueueSize(): Promise<void> {
    try {
      const ops = await queue.list();
      queueSize = ops.length;
      recompute();
    } catch (err) {
      console.error("[sync-status] queue read failed", err);
    }
  }

  const onOnline = (): void => {
    recompute();
  };
  const onOffline = (): void => {
    recompute();
  };
  const offQueue = queue.on("change", () => {
    void refreshQueueSize();
  });

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  // Seed initial queue size asynchronously.
  void refreshQueueSize();

  return {
    get: (): SyncStatus => last,
    on(event, fn): () => void {
      if (event !== "change") return (): void => {};
      listeners.add(fn);
      return (): void => {
        listeners.delete(fn);
      };
    },
    setDraining(next): void {
      if (draining === next) return;
      draining = next;
      recompute();
    },
    destroy(): void {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      offQueue();
      listeners.clear();
    },
  };
}
